import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import useEmployeeDetails from "../hooks/useEmployeeDetails";
import { Coffee, Utensils, Cookie, MoreHorizontal, Clock } from "lucide-react";


const BreakTimer = () => {
  const [breakType, setBreakType] = useState("");
  const [breakStarted, setBreakStarted] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState("00:00:00");
  const [history, setHistory] = useState([]);
  const [breakId, setBreakId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(""); // ✅ for API message
  const API_URL = process.env.REACT_APP_BASE_URL;
  const { id, employeeToken } = useEmployeeDetails();
  console.log("endtime",endTime)

  const getBreakIcon = (type) => {
    switch (type) {
      case "Tea":
        return <Coffee className="w-4 h-4 text-green-600" />;
      case "Lunch":
        return <Utensils className="w-4 h-4 text-green-600" />;
      case "Snack":
        return <Cookie className="w-4 h-4 text-green-600" />;
      default:
        return <MoreHorizontal className="w-4 h-4 text-green-600" />;
    }
  };


  // ✅ Get checkinId from localStorage
  const checkinId = localStorage.getItem("checkin_id");

  // Start Break API
  const handleStartBreak = async () => {
    if (!breakType) {
      setErrorMessage("Please select a break type first.");
      return;
    }
    if (!checkinId) {
      setErrorMessage("No active check-in found. Please check-in first.");
      return;
    }

    try {
      const res = await axios.post(
        `${API_URL}/add_emp_break`,
        {
          checkin_id: checkinId,
          break_type: breakType,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${employeeToken}`,
          },
        }
      );

      // ✅ Save breakId from API response
      setBreakId(res.data.data?._id);

      // ✅ Show API success message
      setSuccessMessage(res.data.message || "Break started");

      const now = new Date(res.data.data?.start_time || new Date());
      setStartTime(now);
      setEndTime(null);
      setBreakStarted(true);
      setElapsedTime("00:00:00");
      setErrorMessage("");
    } catch (error) {
      console.error("Error starting break:", error);
      setErrorMessage(
        error.response?.data?.message || "Unable to start break. Please try again later."
      );
    }
  };

  // End Break API
  const handleEndBreak = async () => {
    if (!breakId) {
      setErrorMessage("No break in progress");
      return;
    }

    try {
      const res = await axios.post(
        `${API_URL}/end_emp_break`,
        { break_id: breakId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${employeeToken}`,
          },
        }
      );

      setSuccessMessage(res.data.message || "Break ended");
      setEndTime(new Date());
      setBreakStarted(false);
      setBreakId(null);
      setErrorMessage("");

      // ✅ Refresh break history from API
      fetchBreakHistory();
    } catch (error) {
      console.error("Error ending break:", error);
      setErrorMessage(
        error.response?.data?.message ||
        "Unable to end break. Please try again later."
      );
    }
  };


  // Stopwatch ticking
  useEffect(() => {
    let timer;
    if (breakStarted && startTime) {
      timer = setInterval(() => {
        const now = new Date();
        const diff = now - startTime;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const seconds = Math.floor((diff / 1000) % 60);
        setElapsedTime(
          `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
            2,
            "0"
          )}:${String(seconds).padStart(2, "0")}`
        );
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [breakStarted, startTime]);

  // ⬆️ Keep all your existing imports & code above

  const fetchBreakHistory = useCallback(async () => {
    try {
      const res = await axios.post(
        `${API_URL}/check_shift_checkin`,
        { user_id: id },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${employeeToken}`,
          },
        }
      );

      if (res.data?.empBreaks) {
        setHistory(res.data.empBreaks);
        localStorage.setItem("checkin_id", res.data.checkin_id);
      }
    } catch (err) {
      console.error("Error fetching break history:", err);
    }
  },[API_URL, employeeToken,id])

  useEffect(() => {
    fetchBreakHistory();
  }, [fetchBreakHistory]);


  // 🔔 Buzz/Ping every 30 min
  useEffect(() => {
    let buzzInterval;
    if (breakStarted) {
      buzzInterval = setInterval(() => {
        alert("⏰ Reminder: You’ve been on break for 30 minutes!");
        // Optionally play a sound:
        // new Audio("/ping.mp3").play();
      }, 30 * 60 * 1000);
    }
    return () => clearInterval(buzzInterval);
  }, [breakStarted]);

  const formatTime = (date) => {
    if (!date) return "--:--";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="flex flex-col items-center justify-start min-h-screen bg-gradient-to-b from-white via-green-50 to-green-100 px-2 py-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-4 text-center mb-3">
        <h2 className="text-2xl font-bold text-green-700 mb-6">
          Break Time Tracker
        </h2>

        {/* ✅ Success Message */}
        {successMessage && (
          <p className="text-green-600 text-sm font-medium mb-4">
            {successMessage}
          </p>
        )}

        {/* Error Message */}
        {errorMessage && (
          <p className="text-red-600 text-sm font-medium mb-4">
            {errorMessage}
          </p>
        )}

        {/* Break Selector */}
        {!breakStarted && (
          <div className="mb-6">
            <label className="block text-sm text-gray-600 mb-2 font-medium">
              Select Break Type
            </label>
            <select
              value={breakType}
              onChange={(e) => setBreakType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 text-gray-700 focus:ring-2 focus:ring-green-500"
            >
              <option value="">-- Choose Break --</option>
              <option value="Tea">☕ Tea</option>
              <option value="Lunch">🍱 Lunch</option>
              <option value="Snack">🍪 Snack</option>
              <option value="Other">✨ Other</option>
            </select>
          </div>
        )}

        {/* Timer UI */}
        {breakStarted ? (
          <>
            <div className="mt-4">
              <p className="text-sm text-gray-500">Break Started At</p>
              <p className="text-lg font-medium text-gray-700">
                {formatTime(startTime)}
              </p>
            </div>
            <div className="mt-6">
              <p className="text-sm text-gray-500">Elapsed Time</p>
              <p className="text-4xl font-extrabold text-green-600 tracking-widest">
                {elapsedTime}
              </p>
            </div>
            <button
              onClick={handleEndBreak}
              className="mt-6 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded-full transition-all duration-300"
            >
              End Break
            </button>
          </>
        ) : (
          <button
            onClick={handleStartBreak}
            disabled={!breakType || !checkinId} // ✅ disable until valid
            className={`mt-6 ${!breakType || !checkinId
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700"
              } text-white font-semibold py-2 px-6 rounded-full transition-all duration-300`}
          >
            Start Break
          </button>
        )}
      </div>

      {/* Break History - Compact Mobile UI */}
      {history.length > 0 && (
        <div className="w-full max-w-md mx-auto mt-2">
          <h3 className="text-base font-bold text-gray-700 mb-3 text-center">
            Break History
          </h3>

          <div className="divide-y divide-gray-200 border rounded-lg bg-white shadow-sm">
            {history.map((entry, index) => (
              <div
                key={index}
                className="p-3 flex items-center justify-between hover:bg-gray-50 transition"
              >
                {/* Left - Icon + Details */}
                <div className="flex items-center space-x-3 flex-1">
                  {/* Icon */}
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full">
                    {getBreakIcon(entry.break_type)}
                  </div>

                  {/* Text */}
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      {entry.break_type}
                    </p>
                    <p className="text-xs text-gray-500">
                      {entry.start_time
                        ? new Date(entry.start_time).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                        : "--"}{" "}
                      →{" "}
                      {entry.end_time
                        ? new Date(entry.end_time).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                        : "--"}
                    </p>
                  </div>
                </div>

                {/* Right - Duration */}
                <div className="ml-3 flex items-center text-green-600">
                  <Clock className="w-4 h-4 mr-1" />
                  <span className="text-sm font-bold">
                    {entry.duration_in_mins ? `${entry.duration_in_mins}m` : "--"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default BreakTimer;
